/**
 * TRUVIV ECOMMERCE INTERACTIONS & UI ENGINE
 * High-performance, zero-dependency JavaScript for luxury user experience
 */

(function () {
  'use strict';

  // --- Sticky Header Scroll Controller ---
  function initStickyHeader() {
    const header = document.querySelector('.tv-header-wrapper');
    if (!header) return;

    let lastScrollY = window.scrollY;
    let ticking = false;

    function onScroll() {
      const currentScrollY = window.scrollY;

      if (currentScrollY > 40) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }

      if (currentScrollY > 180 && currentScrollY > lastScrollY) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }

      lastScrollY = Math.max(0, currentScrollY);
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(onScroll);
        ticking = true;
      }
    }, { passive: true });
  }

  // --- Announcement Bar Rotator ---
  function initAnnouncementBar() {
    const bar = document.querySelector('.tv-announcement-rotator');
    if (!bar) return;

    const items = bar.querySelectorAll('.tv-announcement-item');
    if (items.length <= 1) return;

    let currentIndex = 0;
    const intervalTime = parseInt(bar.dataset.interval || '4500', 10);

    setInterval(function () {
      items[currentIndex].classList.remove('is-active');
      currentIndex = (currentIndex + 1) % items.length;
      items[currentIndex].classList.add('is-active');
    }, intervalTime);
  }

  // --- Mobile Menu Drawer ---
  function initMobileMenu() {
    const toggleBtns = document.querySelectorAll('[data-tv-drawer-trigger="mobile-menu"]');
    const drawer = document.getElementById('tv-mobile-drawer');
    const closeBtns = document.querySelectorAll('[data-tv-drawer-close]');
    const backdrop = document.querySelector('.tv-mobile-drawer-backdrop');

    if (!drawer) return;

    function openMenu() {
      drawer.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      document.body.classList.add('tv-drawer-locked');
      document.body.style.overflow = 'hidden';
    }

    function closeMenu() {
      drawer.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.classList.remove('tv-drawer-locked');
      document.body.style.overflow = '';
    }

    toggleBtns.forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (drawer.classList.contains('is-open')) {
          closeMenu();
        } else {
          openMenu();
        }
      });
    });

    closeBtns.forEach(btn => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closeMenu();
      });
    });

    if (backdrop) {
      backdrop.addEventListener('click', closeMenu);
    }

    // Accordions inside mobile menu
    const accordions = drawer.querySelectorAll('.tv-mobile-menu-parent');
    accordions.forEach(item => {
      const toggle = item.querySelector('.tv-mobile-menu-toggle');
      if (toggle) {
        toggle.addEventListener('click', function (e) {
          e.preventDefault();
          const parent = item;
          const isOpen = parent.classList.contains('is-expanded');
          accordions.forEach(a => a.classList.remove('is-expanded'));
          if (!isOpen) {
            parent.classList.add('is-expanded');
          }
        });
      }
    });
  }

  // --- Lifestyle Tabs Controller ---
  function initLifestyleTabs() {
    const sections = document.querySelectorAll('.tv-lifestyle-section');
    sections.forEach(sec => {
      const tabs = sec.querySelectorAll('[data-tv-tab]');
      const panes = sec.querySelectorAll('[data-tv-pane]');

      tabs.forEach(tab => {
        tab.addEventListener('click', function (e) {
          e.preventDefault();
          const target = this.dataset.tvTab;

          tabs.forEach(t => {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
          });
          panes.forEach(p => {
            p.classList.remove('is-active');
            p.hidden = true;
          });

          this.classList.add('is-active');
          this.setAttribute('aria-selected', 'true');

          const activePane = sec.querySelector(`[data-tv-pane="${target}"]`);
          if (activePane) {
            activePane.classList.add('is-active');
            activePane.hidden = false;
          }
        });
      });
    });
  }

  // --- FAQ Accordions ---
  function initFaqAccordions() {
    const accordions = document.querySelectorAll('.tv-faq-item');
    accordions.forEach(item => {
      const button = item.querySelector('.tv-faq-trigger');
      const content = item.querySelector('.tv-faq-content');
      if (!button || !content) return;

      button.addEventListener('click', function () {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';

        // Close siblings if in single mode
        const parent = item.closest('.tv-faq-list');
        if (parent && parent.dataset.exclusive === 'true') {
          parent.querySelectorAll('.tv-faq-trigger').forEach(b => {
            b.setAttribute('aria-expanded', 'false');
            const c = b.closest('.tv-faq-item').querySelector('.tv-faq-content');
            if (c) c.hidden = true;
          });
        }

        if (isExpanded) {
          button.setAttribute('aria-expanded', 'false');
          content.hidden = true;
        } else {
          button.setAttribute('aria-expanded', 'true');
          content.hidden = false;
        }
      });
    });
  }

  // --- Video Modal Player (Safe On-Demand Lazy Initialization) ---
  function initVideoModals() {
    const triggers = document.querySelectorAll('[data-tv-video-trigger]');
    if (!triggers || !triggers.length) return; // Strict safeguard: never inject DOM if no video triggers exist

    function getOrCreateModal() {
      let modal = document.getElementById('tv-video-modal-global');
      if (!modal) {
        modal = document.createElement('div');
        modal.id = 'tv-video-modal-global';
        modal.className = 'tv-modal-backdrop';
        modal.style.display = 'none';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.innerHTML = `
          <div class="tv-modal-dialog">
            <button type="button" class="tv-modal-close" aria-label="Close modal">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
            <div class="tv-modal-media" style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; background: #000; border-radius: 12px;">
              <iframe id="tv-modal-iframe-global" src="" style="position: absolute; top:0; left: 0; width: 100%; height: 100%; border:0;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"></iframe>
              <video id="tv-modal-video-global" controls playsinline style="display:none; position: absolute; top:0; left: 0; width: 100%; height: 100%;"></video>
            </div>
          </div>
        `;
        document.body.appendChild(modal);

        const closeBtn = modal.querySelector('.tv-modal-close');
        function closeModal() {
          modal.classList.remove('is-active');
          modal.style.display = 'none';
          const iframe = document.getElementById('tv-modal-iframe-global');
          const video = document.getElementById('tv-modal-video-global');
          if (iframe) iframe.src = '';
          if (video) {
            video.pause();
            video.src = '';
            video.style.display = 'none';
          }
          document.body.style.overflow = '';
        }

        closeBtn.addEventListener('click', closeModal);
        modal.addEventListener('click', function (e) {
          if (e.target === modal) closeModal();
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && modal.classList.contains('is-active')) closeModal();
        });
      }
      return modal;
    }

    triggers.forEach(trigger => {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        const videoUrl = this.dataset.tvVideoUrl || this.href;
        if (!videoUrl) return;

        const modal = getOrCreateModal();
        const iframe = document.getElementById('tv-modal-iframe-global');
        const video = document.getElementById('tv-modal-video-global');

        if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') || videoUrl.includes('vimeo.com')) {
          let embedUrl = videoUrl;
          if (videoUrl.includes('watch?v=')) {
            embedUrl = videoUrl.replace('watch?v=', 'embed/') + '?autoplay=1';
          } else if (videoUrl.includes('youtu.be/')) {
            embedUrl = videoUrl.replace('youtu.be/', 'www.youtube.com/embed/') + '?autoplay=1';
          }
          if (video) video.style.display = 'none';
          if (iframe) {
            iframe.style.display = 'block';
            iframe.src = embedUrl;
          }
        } else {
          // Direct MP4
          if (iframe) {
            iframe.src = '';
            iframe.style.display = 'none';
          }
          if (video) {
            video.style.display = 'block';
            video.src = videoUrl;
            video.play();
          }
        }

        modal.style.display = 'flex';
        modal.classList.add('is-active');
        document.body.style.overflow = 'hidden';
      });
    });
  }

  // --- Quick Add To Cart AJAX ---
  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.js-ajax-add-to-cart, [data-tv-quick-add], .truviv-card-quick-add-btn');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const form = btn.closest('form');
      const variantId = btn.dataset.variantId || btn.dataset.tvVariantId || form?.querySelector('[name="id"]')?.value;
      if (!variantId) {
        console.warn('Truviv Quick Add: No variant ID found on element', btn);
        return;
      }

      const originalHtml = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = `<span style="display:inline-block;width:14px;height:14px;border:2px solid rgba(255,255,255,0.35);border-top-color:#ffffff;border-radius:50%;animation:tvSpinQuick 0.6s linear infinite;margin-right:6px;vertical-align:middle;"></span> Adding...`;

      // Request pre-rendered cart sections for Dawn cart drawer
      const requestData = {
        id: variantId,
        quantity: parseInt(btn.dataset.quantity || '1', 10),
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      };

      fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      })
      .then(async res => {
        const data = await res.json();
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || 'Could not add item to cart.');
        }
        return data;
      })
      .then(data => {
        btn.innerHTML = `✓ Added`;
        btn.classList.add('is-success');

        // Check for direct checkout intent
        if (btn.dataset.action === 'checkout') {
          window.location.href = '/checkout';
          return;
        }

        // Open & refresh Dawn cart drawer if available
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.classList.remove('is-empty');
          if (cartDrawer.querySelector('.drawer__inner')) {
            cartDrawer.querySelector('.drawer__inner').classList.remove('is-empty');
          }
        }
        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
          cartDrawer.renderContents(data);
        } else {
          // Manual fallback: refresh cart bubbles
          fetch(window.routes?.cart_url || '/cart.js')
            .then(res => res.json())
            .then(cart => {
              const bubbles = document.querySelectorAll('.tv-cart-bubble, .cart-count-bubble, #cart-icon-bubble span');
              bubbles.forEach(b => {
                b.textContent = cart.item_count;
                b.classList.remove('visually-hidden', 'hidden');
              });

              if (cartDrawer && typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              } else {
                const cartNotification = document.querySelector('cart-notification');
                if (cartNotification && typeof cartNotification.open === 'function') {
                  cartNotification.open();
                }
              }
            })
            .catch(() => {});
        }

        setTimeout(() => {
          btn.disabled = false;
          btn.innerHTML = originalHtml;
          btn.classList.remove('is-success');
        }, 2200);
      })
      .catch(err => {
        console.error('Truviv Quick Add Error:', err);
        btn.disabled = false;
        btn.innerHTML = originalHtml;
        btn.classList.remove('is-success');
        alert(err.message || 'Could not add item to cart. Please try again.');
      });
    });
  }

  // --- Product Page Gallery Controller ---
  function initProductGallery() {
    const gallery = document.querySelector('.tv-pdp-gallery');
    if (!gallery) return;

    const mainImg = gallery.querySelector('.tv-pdp-main-media img');
    const thumbs = gallery.querySelectorAll('.tv-pdp-thumb-btn');

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', function () {
        const fullSrc = this.dataset.fullSrc || this.querySelector('img')?.src;
        if (fullSrc && mainImg) {
          mainImg.style.opacity = '0';
          setTimeout(() => {
            mainImg.src = fullSrc;
            mainImg.style.opacity = '1';
          }, 150);
        }
        thumbs.forEach(t => t.classList.remove('is-active'));
        this.classList.add('is-active');
      });
    });
  }

  // --- Sticky Mobile Add-to-Cart Observer ---
  function initStickyMobileCart() {
    const mainBtn = document.querySelector('.tv-pdp-add-to-cart-btn');
    const stickyBar = document.querySelector('.tv-sticky-bar');
    if (!mainBtn || !stickyBar) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          // If main button is NOT intersecting and is above the viewport, show sticky bar
          if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
            stickyBar.classList.add('is-visible');
          } else {
            stickyBar.classList.remove('is-visible');
          }
        });
      }, { threshold: 0 });
      observer.observe(mainBtn);
    } else {
      window.addEventListener('scroll', function () {
        const rect = mainBtn.getBoundingClientRect();
        if (rect.top < 0) {
          stickyBar.classList.add('is-visible');
        } else {
          stickyBar.classList.remove('is-visible');
        }
      }, { passive: true });
    }
  }

  // --- PDP Accordion Controller ---
  function initProductAccordions() {
    const accordionHeaders = document.querySelectorAll('.tv-pdp-accordion-header');
    accordionHeaders.forEach(header => {
      header.addEventListener('click', function () {
        const item = this.closest('.tv-pdp-accordion-item');
        if (!item) return;
        const isOpen = item.classList.contains('is-open');

        // Close siblings if desired or allow multi-expand
        item.classList.toggle('is-open', !isOpen);
      });
    });
  }

  // --- PDP Quantity Stepper ---
  function initQuantitySteppers() {
    const steppers = document.querySelectorAll('.tv-pdp-qty-stepper');
    steppers.forEach(stepper => {
      const minusBtn = stepper.querySelector('[data-qty-action="minus"]');
      const plusBtn = stepper.querySelector('[data-qty-action="plus"]');
      const input = stepper.querySelector('.tv-pdp-qty-input');
      if (!input) return;

      if (minusBtn) {
        minusBtn.addEventListener('click', () => {
          let val = parseInt(input.value, 10) || 1;
          if (val > 1) {
            input.value = val - 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', () => {
          let val = parseInt(input.value, 10) || 1;
          if (val < 99) {
            input.value = val + 1;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    });
  }

  // --- Comparison Finder Quiz ---
  function initComparisonQuiz() {
    const finderTabs = document.querySelectorAll('.tv-finder-tab');
    if (!finderTabs.length) return;

    finderTabs.forEach(tab => {
      tab.addEventListener('click', function () {
        finderTabs.forEach(t => t.classList.remove('is-active'));
        this.classList.add('is-active');

        const targetModel = this.dataset.targetModel;
        const targetCol = document.querySelector(`[data-compare-col="${targetModel}"]`);
        if (targetCol) {
          // Remove highlight from all
          document.querySelectorAll('.tv-compare-col-header, .tv-compare-cell').forEach(el => {
            el.classList.remove('tv-compare-highlight-col');
          });

          // Highlight selected
          document.querySelectorAll(`[data-compare-col="${targetModel}"]`).forEach(el => {
            el.classList.add('tv-compare-highlight-col');
          });

          targetCol.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });
    });
  }

  // --- Cart Drawer Triggers ---
  function initCartDrawerTriggers() {
    const cartTriggers = document.querySelectorAll('.tv-cart-trigger, #cart-icon-bubble');
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    cartTriggers.forEach(trigger => {
      trigger.addEventListener('click', function (e) {
        if (typeof drawer.open === 'function') {
          e.preventDefault();
          drawer.open();
        }
      });
    });
  }

  // Initialize all modules when DOM is ready
  function init() {
    initStickyHeader();
    initAnnouncementBar();
    initMobileMenu();
    initLifestyleTabs();
    initFaqAccordions();
    initVideoModals();
    initQuickAdd();
    initProductGallery();
    initStickyMobileCart();
    initProductAccordions();
    initQuantitySteppers();
    initComparisonQuiz();
    initCartDrawerTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify theme editor events
  document.addEventListener('shopify:section:load', init);
})();
